import { Fragment, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { AiAssist } from './AiAssist'
import { FindingScope, FindingSubject } from './FindingScope'
import { Link } from 'react-router'
import { Button, Card, Checkbox, Row, Table, TableScroll, Tag, Td, Th } from '../../ui'
import { evidenceBlocks, type EvidenceTableData } from './evidenceTable'
import {
  decideFinding,
  getFinding,
  getOverview,
  getWorkspace,
  referenceOf,
  setFindingWorkflow,
} from '../../lib/api'
import type {
  FindingDetailResponse,
  FindingDocLink,
  FindingRecheck,
  FindingSummary,
  WorkspaceMember,
} from '../../lib/api'
import { formatDay, severityTag } from './LiveDashboard'
import { effortChip, formatDate } from './LiveFindings'
import { describeFailure, useApi } from './useApi'
import { daysOpen, passedTarget } from './FindingsInsights'
import { severitySourceLabel } from './labels'
import './live.css'
import './finding-detail.css'


/**
 * A finding's provenance as three INDEPENDENT axes, always rendered together so none is silently
 * dropped:
 *   · FRAMEWORK — the CISA SCuBA policy it maps to, or "Outside CISA SCuBA" (never borrowing an
 *     authority it does not have);
 *   · SEVERITY — who judged the severity ('defenssive' even when the underlying evidence is
 *     Microsoft's, e.g. a Secure Score band; only Defender's CVE severities are genuinely Microsoft's);
 *   · EVIDENCE — where the raw signal originates (migration 0145): a Microsoft-produced signal, or a
 *     Microsoft 365 setting Defenssive read and evaluated itself.
 * The fixed disclosure keeps the honest boundary: the data may be Microsoft's, the conclusion is
 * Defenssive's, and ScubaGear is never run. Every axis renders ALWAYS and fails CLOSED — a field the
 * API did not send (an older response: `undefined`) reads "Not recorded" rather than vanishing or
 * inventing an origin. `null` is a real answer and is distinct: framework `null` means "Outside CISA
 * SCuBA" (a deliberate additional check), which is NOT the same as "we don't know".
 */
export function FindingProvenance({
  scubaReference,
  severitySource,
  evidenceSources,
}: {
  scubaReference: string | null | undefined
  severitySource: string | null | undefined
  evidenceSources: string[] | undefined
}) {
  // Each label is the QUESTION the line answers (owner, 2026-09-25: "what is severity: Defenssive").
  // "Severity: Defenssive" read as if the severity were called Defenssive; "Severity set by" says
  // what the word means. Same three axes, same fail-closed values, plain words.
  return (
    <div className="finding-provenance" style={{ marginTop: 'var(--space-2)' }}>
      <p className="finding-identifiers finding-source">
        <span className="finding-id-label">Standard</span>
        {scubaReference ? (
          <span>
            CISA SCuBA policy <code>{scubaReference}</code>
          </span>
        ) : scubaReference === null && severitySource === 'prowler' ? (
          // A Cloud (Azure) check: open-source, used as published, and not ours either.
          <span className="finding-source-additional">
            Not part of CISA SCuBA — an open-source Azure check, used as published
          </span>
        ) : scubaReference === null ? (
          <span className="finding-source-additional">
            Not part of CISA SCuBA — one of Defenssive&apos;s own checks
          </span>
        ) : (
          <span>Not recorded</span>
        )}
      </p>

      <p className="finding-identifiers finding-source">
        <span className="finding-id-label">Severity set by</span>
        <span>
          {severitySourceLabel(severitySource)}
          {severitySource === 'defenssive' && scubaReference
            ? " — CISA's baseline says which policies are required but doesn't rate severity, so the rating is ours"
            : severitySource && severitySource !== 'defenssive'
              ? ', unchanged by Defenssive'
              : null}
        </span>
      </p>

      <p className="finding-identifiers finding-source">
        <span className="finding-id-label">Checked using</span>
        <span>
          {evidenceSources !== undefined && evidenceSources.length > 0
            ? evidenceSources.join(' · ')
            : 'Not recorded'}
        </span>
      </p>

      {/* Only where CISA is named: Defenssive checks the policy itself, and a reader comparing
          against CISA's own tool should know the two are separate implementations. */}
      {scubaReference ? (
        <p className="finding-evidence-note settings-note">
          Defenssive checks this against the CISA policy itself; it does not run CISA&apos;s ScubaGear tool.
        </p>
      ) : null}
    </div>
  )
}

/**
 * One finding, in full — and the place a person acts on it.
 *
 * Evidence is fetched here and nowhere else. It carries the customer's own identifiers — user
 * principal names, device names, the contents of a policy — so it arrives only when somebody
 * opened this one finding, rather than riding along with every title in every list.
 *
 * This page also carries the two writes in the findings model: a DISPOSITION (setting a finding
 * aside, or bringing it back) and the WORKING STATE (owner, progress, ticked steps). The line
 * between them is deliberate and visible on screen: setting a finding aside takes it off the
 * active list, because a person took responsibility for it; marking the work "done" does NOT,
 * because only a scan may say a finding is gone. The product never reports clean on a say-so.
 */
export function LiveFindingDetail({
  reference,
  finding: known,
  onBack,
  backLabel = 'Back',
}: {
  /** The finding's reference or whole id — whatever the url carries. */
  reference: string

  /**
   * The row that was clicked, when there was one. Only to paint the heading before the fetch
   * answers; arriving without it is the normal case (a url someone was sent has a reference and
   * nothing else), so nothing on this page may depend on it.
   */
  finding?: FindingSummary

  onBack: () => void

  /**
   * What the back button says: where onBack goes ("Back to dashboard", "Back to Email"). It said
   * "Back to findings" wherever it went, so a person who opened a finding from the dashboard was
   * promised a list and landed on the dashboard. A plain "Back" when the caller does not say.
   */
  backLabel?: string
}) {
  // Bumped after every successful write so the detail re-fetches and the server's copy — the new
  // status, the recorded decision — replaces what was on screen. useApi keys off its deps.
  const [refresh, setRefresh] = useState(0)
  // keepPrevious: a save calls reload(), which bumps `refresh` and re-fetches. Without it the whole
  // ready body would unmount to a spinner on every Save — losing the in-place "Saved." and flashing.
  const detail = useApi(() => getFinding(reference), [reference, refresh], undefined, true)
  const reload = () => setRefresh((n) => n + 1)

  // The remediation targets (14/30/60/90 days by severity), from the same overview the lists read, so
  // "past the target" here is the server's rule. Fails soft: no targets, no "past" -- never a guess.
  const overview = useApi(() => getOverview(), [])
  const targets =
    overview.state === 'ready' && overview.data && typeof overview.data.remediationTargetDays === 'object'
      ? overview.data.remediationTargetDays
      : undefined

  // What the person just did here, so the page can say it where they will hear it. Setting aside
  // and reopening both move the finding's controls to a different part of the page (or remove
  // them), which used to drop keyboard focus onto the page body and confirm nothing near the
  // button that was pressed. Null for a page opened from a link -- that must not steal focus.
  const [justDecided, setJustDecided] = useState<'set_aside' | 'reopened' | null>(null)
  // Belongs to the finding it was said about. The page is reused when the header search opens
  // another finding, which must not arrive announcing a decision nobody made on it.
  useEffect(() => setJustDecided(null), [reference])

  // The server's copy wins once it arrives. The clicked row is a snapshot from a list fetched
  // some time ago, and the two disagreeing about severity or status on one screen is the kind of
  // thing a customer screenshots.
  const finding = detail.state === 'ready' ? detail.data.finding : known

  const back = (
    <Button variant="ghost" onClick={onBack}>
      {/* The arrow is a picture of the word beside it; a screen reader hears only the words. */}
      <span aria-hidden="true">←</span> {backLabel}
    </Button>
  )

  if (finding === undefined) {
    return (
      <>
        {back}
        <div
          className={detail.state === 'error' ? 'live-error' : 'live-empty'}
          style={{ marginTop: 'var(--space-4)' }}
        >
          {detail.state === 'error' ? describeFailure(detail.status) : 'Loading…'}
        </div>
      </>
    )
  }

  // The owner as last saved: the server's working state once it arrives, else the clicked row's.
  const savedOwner =
    detail.state === 'ready' && detail.data.workflow ? detail.data.workflow.owner : finding.owner

  return (
    <>
      {back}

      <Card title={finding.title} style={{ marginTop: 'var(--space-4)' }}>
        {/* Names come only from this finding's recorded evidence, not from its claim or a guess. */}
        {detail.state === 'ready' ? <FindingSubject evidence={detail.data.evidence} /> : null}
        <FindingScope summary={finding.summary} />

        {/* ONE line of facts (finding page layout, owner 2026-10-11): what a person decides on first.
            Where it came from and when it was first and last seen are under Details below. */}
        <div className="finding-facts">
          {severityTag(finding.severity)}
          {/* ALWAYS the status. The route exists so a finding can be bookmarked and shared, and a
              resolved one opened from a link looked exactly like an open one -- red tag, "How to
              fix it" -- so somebody who had already fixed it could not tell, and might redo it. */}
          {/* A finding closed as not applicable (0176), under the organisation profile or because of
              the tenant's licences, is not "Resolved": the tag says what the paragraph below says, and
              does not wear the colour of a fix. */}
          {finding.status === 'resolved' && finding.closedAs === 'not_applicable' ? (
            <Tag tone="outline">Closed as not applicable</Tag>
          ) : (
            <Tag tone={finding.status === 'resolved' ? 'accent' : 'outline'}>
              {STATUS_LABEL[finding.status] ?? finding.status}
            </Tag>
          )}
          {finding.effort ? effortChip(finding.effort) : null}
          {finding.status === 'active' ? (
            <>
              <OpenFor finding={finding} targets={targets} />
              {savedOwner !== undefined ? (
                <span className={savedOwner ? 'finding-fact' : 'finding-fact finding-fact-muted'}>
                  {savedOwner ?? 'No owner'}
                </span>
              ) : null}
            </>
          ) : (
            <span className="finding-fact finding-fact-muted">Last seen {formatDate(finding.lastSeenAt)}</span>
          )}
        </div>

        {/* Closed as not applicable is NOT fixed (0175, 0176): the assessment that closed it recorded
            the control as not applicable, under the organisation profile or because of the tenant's
            licences (owner, 2026-09-29), with its own reason naming which. Framed as a record of that
            assessment, because the reason is its words about the workspace and the tenant then, and
            the page is read later. Never "no longer sees this". */}
        {finding.status === 'resolved' && detail.state === 'ready' && detail.data.noLongerApplies ? (
          <p style={{ fontSize: 13, marginTop: 'var(--space-2)' }}>
            The assessment of {formatDate(detail.data.noLongerApplies.resolvedAt)} closed this as not
            applicable, giving this reason: {detail.data.noLongerApplies.reason} It was not marked
            fixed, and it is not on the active list or in the severity counts.
          </p>
        ) : finding.status === 'resolved' ? (
          <p style={{ fontSize: 13, marginTop: 'var(--space-2)' }}>
            A completed assessment no longer sees this
            {finding.resolvedAt ? <> — resolved {formatDate(finding.resolvedAt)}</> : null}. It is
            not on the active list and not in the severity counts.
          </p>
        ) : null}

        {/* Withdrawn is NOT resolved. The check was retired, so no assessment looks any more -- and
            nothing saw this go away. Said plainly, because a finding that quietly stopped appearing
            is otherwise indistinguishable from one somebody fixed. */}
        {finding.status === 'withdrawn' ? (
          <p style={{ fontSize: 13, marginTop: 'var(--space-2)' }}>
            Defenssive stopped assessing this
            {finding.withdrawnAt ? <> on {formatDay(finding.withdrawnAt)}</> : null} — its check was
            retired. It was last seen {formatDate(finding.lastSeenAt)}. This is kept as history; it
            was not marked fixed.
          </p>
        ) : null}

        {/* Active, but the latest finished assessment did not re-check it (identity review,
            2026-09-25: one finding sat at "last seen 8 September" beside every other at the 24th,
            with nothing on the page to say why). The server decides; the page only words it. */}
        {finding.status === 'active' && detail.state === 'ready' && detail.data.recheck ? (
          <RecheckNotice recheck={detail.data.recheck} />
        ) : null}

        {/* The job this finding is part of, and the one ordering caveat it carries — so a person
            fixing it knows it travels with others and in what order. A long caveat is folded: it is
            read once, and it pushed the steps a screen down. */}
        {finding.workstream ? (
          <Workstream name={finding.workstream} hint={finding.sequenceHint} />
        ) : null}

        <details className="finding-details">
          <summary>Details</summary>
          <p className="finding-identifiers">
            <span className="finding-id-label">Area</span>
            <span>{finding.area}</span>
            <span className="finding-id-label">First seen</span>
            <span>{formatDate(finding.firstSeenAt)}</span>
            <span className="finding-id-label">Last seen</span>
            <span>{formatDate(finding.lastSeenAt)}</span>
          </p>

          {/* The reference: this tenant's instance of the finding, and what to quote at us. The
              internal check key is not shown on screen (owner's call, 2026-09-25) -- the CISA policy,
              where there is one, is the Standard line below; the key stays in the CSV export. */}
          <p className="finding-identifiers" style={{ marginTop: 'var(--space-2)' }}>
            <span className="finding-id-label">Reference</span>
            <code>{referenceOf(finding.id)}</code>
          </p>

          {/* Provenance: three independent axes, shown together so none is silently omitted, each failing
              CLOSED to "Not recorded" for a field an older API response did not send. See FindingProvenance. */}
          {detail.state === 'ready' ? (
            <FindingProvenance
              scubaReference={detail.data.scubaReference}
              severitySource={finding.severitySource}
              evidenceSources={detail.data.evidenceSources}
            />
          ) : null}
        </details>
      </Card>

      {detail.state === 'loading' ? <div className="live-empty">Loading…</div> : null}

      {detail.state === 'error' ? (
        <div className="live-error" style={{ marginTop: 'var(--space-4)' }}>
          {describeFailure(detail.status)}
        </div>
      ) : null}

      {/* KEYED by the finding. This page is reused when the header search opens another finding,
          and an unkeyed body carried its local state across: a Set aside left "Recording…", or one
          finding's ticked steps and owner offered for saving onto the next. */}
      {detail.state === 'ready' ? (
        <FindingBody
          key={finding.id}
          finding={finding}
          data={detail.data}
          justDecided={justDecided}
          onDecided={(what) => {
            setJustDecided(what)
            reload()
          }}
          onChanged={reload}
        />
      ) : null}
    </>
  )
}

/**
 * Everything under the header, once the detail has arrived (finding page layout, owner 2026-10-11).
 *
 * Read in the order a person works: what it means, how to fix it, then what we saw. On a wide screen
 * an active finding's work -- status, owner, how many steps are ticked, Save, Set this aside -- sits
 * in a side panel that stays in view while the steps scroll. On a narrow one that panel comes
 * straight after the steps, so Save is where the ticking ended (the order is in finding-detail.css).
 */
function FindingBody({
  finding,
  data,
  justDecided,
  onDecided,
  onChanged,
}: {
  finding: FindingSummary
  data: FindingDetailResponse
  justDecided: 'set_aside' | 'reopened' | null
  onDecided: (what: 'set_aside' | 'reopened') => void
  onChanged: () => void
}) {
  const active = finding.status === 'active'
  const tracking = active && data.remediation.length > 0
  const work = useWork(finding.id, data.workflow, onChanged)
  // Whether the AI card drew anything: nothing at all while bring-your-own AI is off, and then the
  // side panel offers no way to it either.
  const [aiShown, setAiShown] = useState(false)
  const sections = [
    { id: 'finding-meaning', label: 'Overview', shown: Boolean(data.description) },
    { id: 'finding-evidence', label: 'Evidence', shown: data.evidence.length > 0 },
    { id: 'finding-fix', label: finding.status === 'resolved' || finding.status === 'withdrawn' ? 'Steps for reference' : 'Fix steps', shown: data.remediation.length > 0 },
    { id: 'finding-progress', label: 'Progress', shown: tracking },
    { id: 'finding-ai', label: 'Your AI', shown: aiShown },
    { id: 'finding-references', label: 'References', shown: referenceChips(data.references).length > 0 || (data.docLinks ?? []).some((link) => isWebAddress(link.url)) },
  ].filter((section) => section.shown)

  return (
    <div className="finding-frame">
      {sections.length > 0 ? <nav className="finding-section-nav" aria-label="Finding sections">
        <span className="finding-section-label">Jump to</span>
        {sections.map((section) => (
          <a key={section.id} href={`#${section.id}`} onClick={(event) => {
            if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return
            event.preventDefault()
            jumpToFindingSection(section.id)
          }}>{section.label}</a>
        ))}
      </nav> : null}
      <div className={active ? 'finding-layout has-side' : 'finding-layout'}>
        <div className="finding-main">
          {/* The standing decision, when there is one. Directly under the header so a person opening
              a set-aside finding sees WHY before its details, and can bring it back. */}
          {finding.status === 'suppressed' ? (
            <DispositionBanner
              id={finding.id}
              disposition={data.disposition}
              announce={justDecided === 'set_aside'}
              onChanged={() => onDecided('reopened')}
            />
          ) : null}

          {active && justDecided === 'reopened' ? <ReopenedNote /> : null}

          {data.description ? (
            <Card id="finding-meaning" tabIndex={-1} title="What this means" className="finding-slot-means finding-section-target" style={{ marginTop: 'var(--space-4)' }}>
              <Meaning text={data.description} />
            </Card>
          ) : null}

          {/* How to fix it, BEFORE what we saw (owner, 2026-10-11): the steps are what a person came
              for, and the evidence is there to check against once they know what to change. When the
              finding is active each step is a checkbox. Set aside, the steps show read-only, because
              there is no work in flight to track. Resolved and withdrawn keep them only for
              reference: nothing assesses a withdrawn finding, so work tracked on it could never be
              confirmed by a scan. */}
          {data.remediation.length > 0 ? (
            tracking ? (
              <WorkSteps
                work={work}
                remediation={data.remediation}
                stages={data.remediationStages}
                portalUrl={data.portalUrl}
                readOnlyNote={data.evidence.length === 0}
              />
            ) : (
              <Card
                title={
                  finding.status === 'resolved' || finding.status === 'withdrawn'
                    ? 'The steps, for reference'
                    : 'How to fix it'
                }
                id="finding-fix"
                tabIndex={-1}
                className="finding-slot-fix finding-section-target"
                style={{ marginTop: 'var(--space-4)' }}
              >
                {stageGroups(data.remediation, data.remediationStages).map((group) => (
                  <Fragment key={group.start}>
                    {group.title ? <h3 className="finding-stage">{group.title}</h3> : null}
                    {/* start= keeps one numbering across the stages: step 7 is step 7 under any heading. */}
                    <ol start={group.start + 1} style={{ fontSize: 13, lineHeight: 1.6, margin: 0, paddingLeft: '1.25rem' }}>
                      {group.steps.map(({ index, text }) => (
                        <li key={index} style={{ marginBottom: 'var(--space-2)' }}>
                          {renderStep(text)}
                        </li>
                      ))}
                    </ol>
                  </Fragment>
                ))}
                <PortalButton url={data.portalUrl} />
                {data.evidence.length === 0 ? <ReadOnlyNote /> : null}
              </Card>
            )
          ) : null}

          {data.evidence.length > 0 ? (
            <Card id="finding-evidence" tabIndex={-1} title="What we saw" className="finding-slot-saw finding-section-target" style={{ marginTop: 'var(--space-4)' }}>
              {/* Label/value rows as before; a value that is a table (EvidenceTable.cs) is drawn as
                  one, under its label -- "which vulnerabilities, on which devices" is a table, not
                  a sentence (owner, 2026-09-25). Page order is kept. */}
              {evidenceBlocks(data.evidence).map((block, index) =>
                block.kind === 'rows' ? (
                  <table key={`rows-${index}`} className="evidence-table">
                    <tbody>
                      {block.items.map((item, row) => (
                        <tr key={`${item.label}-${row}`}>
                          <th scope="row">{item.label}</th>
                          <td>{item.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <EvidenceTableBlock key={`table-${index}`} label={block.label} table={block.table} />
                ),
              )}
              {/* The one place the page says Defenssive only reads (it said "read-only" three
                  times). Where there is no evidence card, the steps card says it instead -- see
                  ReadOnlyNote. What Defenssive DOES, not "with read-only access": the Terms and the
                  privacy notice say its two Exchange permissions are not read-only by name. */}
              <p style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 'var(--space-3)' }}>
                {finding.status === 'resolved' || finding.status === 'withdrawn' ? 'As last read' : 'Read'} from your tenant on{' '}
                {formatDate(finding.lastSeenAt)}. Defenssive only reads: it makes no change in your
                tenant.
              </p>
            </Card>
          ) : null}

          {/* Explain and Fix plan with the workspace's own Azure OpenAI: nothing at all while that is
              off. In the main column, because its answers are paragraphs. */}
          <div id="finding-ai" className="finding-slot-ai finding-section-target" tabIndex={-1}>
            <AiAssist findingId={finding.id} onShown={setAiShown} />
          </div>

          <div id="finding-references" tabIndex={-1} className="finding-slot-refs finding-section-target">
            <References references={data.references} docLinks={data.docLinks ?? []} />
          </div>
        </div>

        {/* Setting a finding aside takes it off the active list. Offered only when it is on that
            list — a set-aside finding is brought back from the banner, not here. */}
        {active ? (
          <aside className="finding-side" aria-label="Work on this finding">
            {tracking ? <WorkPanel work={work} total={data.remediation.length} /> : null}
            {aiShown ? (
              <p className="finding-side-jump">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => jumpToFindingSection('finding-ai')}
                >
                  Explain with your AI
                </button>
              </p>
            ) : null}
            <SetAside id={finding.id} onChanged={() => onDecided('set_aside')} />
          </aside>
        ) : null}
      </div>
    </div>
  )
}

/** In-page navigation only. Never invokes a finding action or sends evidence to a model. */
function jumpToFindingSection(id: string) {
  const section = document.getElementById(id)
  if (!section) return
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  section.scrollIntoView?.({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' })
  section.focus({ preventScroll: true })
}

/**
 * What this means: its first paragraph, and the rest folded (0201). The catalogue writes a long
 * explanation as two paragraphs, what is wrong and why it matters, then how Defenssive judged it and
 * what it can't see. The first is what a person needs to decide; the second is there when they ask.
 */
function Meaning({ text }: { text: string }) {
  const [lead, ...rest] = text.split(/\n\s*\n/).map((p) => p.trim()).filter((p) => p !== '')
  return (
    <>
      <p style={{ fontSize: 14, lineHeight: 1.6, maxWidth: '72ch', margin: 0 }}>{lead}</p>
      {rest.length > 0 ? (
        <details className="finding-more">
          <summary>More about this check</summary>
          {rest.map((paragraph, i) => (
            <p key={i}>{paragraph}</p>
          ))}
        </details>
      ) : null}
    </>
  )
}

type StepGroup = { title: string | null; start: number; steps: { index: number; text: string }[] }

/**
 * The steps split under their stage headings (0201). Each group keeps its steps' own indices, so the
 * ticks still point at the same steps. Steps before the first heading, or every step when there are no
 * headings, form one untitled group. A heading the server would have dropped is dropped here too.
 */
export function stageGroups(steps: string[], stages: FindingDetailResponse['remediationStages']): StepGroup[] {
  const starts: { title: string; firstStep: number }[] = []
  for (const s of stages ?? []) {
    const after = starts.length === 0 || s.firstStep > starts[starts.length - 1]!.firstStep
    if (s.title && s.firstStep >= 0 && s.firstStep < steps.length && after) starts.push(s)
  }
  const groups: StepGroup[] = []
  if (starts.length === 0 || starts[0]!.firstStep > 0) groups.push({ title: null, start: 0, steps: [] })
  for (const s of starts) groups.push({ title: s.title, start: s.firstStep, steps: [] })
  steps.forEach((text, index) => {
    const group = groups.filter((g) => g.start <= index).pop()!
    group.steps.push({ index, text })
  })
  return groups.filter((g) => g.steps.length > 0)
}

/** Rows shown before "Show all": enough to see what kind of thing is listed, short of a wall. */
const EVIDENCE_ROWS = 5

/**
 * One evidence table, under its label, with its total beside the label. A long one shows its first
 * five rows and a button for the rest (owner, 2026-10-11): a hundred affected devices pushed every
 * card after them off the page, and the count already says how many there are.
 */
function EvidenceTableBlock({ label, table }: { label: string; table: EvidenceTableData }) {
  const [all, setAll] = useState(false)
  const tableId = useId()
  const long = table.rows.length > EVIDENCE_ROWS
  const rows = long && !all ? table.rows.slice(0, EVIDENCE_ROWS) : table.rows

  return (
    <section className="evidence-block">
      <h3 className="evidence-block-title">
        {label}
        <span className="evidence-block-count">{table.rows.length}</span>
      </h3>
      <TableScroll label={label}>
        <Table id={tableId}>
          <thead>
            <Row>
              {table.columns.map((column) => (
                <Th key={column}>{column}</Th>
              ))}
            </Row>
          </thead>
          <tbody>
            {rows.map((cells, row) => (
              <Row key={row}>
                {cells.map((cell, column) => (
                  <Td key={column}>{cell}</Td>
                ))}
              </Row>
            ))}
          </tbody>
        </Table>
      </TableScroll>
      {long ? (
        <button
          type="button"
          className="finding-show-all"
          aria-expanded={all}
          aria-controls={tableId}
          onClick={() => setAll((open) => !open)}
        >
          {all ? 'Show the first five' : `Show all ${table.rows.length}`}
        </button>
      ) : null}
      {table.note ? <p className="evidence-block-note">{table.note}</p> : null}
    </section>
  )
}

/** "Open for 34 days", in the critical colour with the target it passed. The list's own words. */
function OpenFor({ finding, targets }: { finding: FindingSummary; targets?: Record<string, number> }) {
  const days = daysOpen(finding.firstSeenAt)
  const passed = passedTarget(finding, targets)
  return (
    <span className={passed !== null ? 'finding-fact finding-age-late' : 'finding-fact'}>
      Open for {days === 1 ? '1 day' : `${days} days`}
      {passed !== null ? `, past the ${passed}-day target` : ''}
    </span>
  )
}

/** Longer than this and the ordering caveat is folded under its workstream. */
const HINT_INLINE = 90

function Workstream({ name, hint }: { name: string; hint: string | null }) {
  const folded = hint !== null && hint.length > HINT_INLINE
  return (
    <div className="finding-workstream">
      Part of <strong style={{ fontWeight: 600 }}>{name}</strong>
      {hint && !folded ? <> · {hint}</> : null}
      {folded ? (
        <details className="finding-workstream-hint">
          <summary>Order of work</summary>
          <p>{hint}</p>
        </details>
      ) : null}
    </div>
  )
}

/**
 * The standing decision on a set-aside finding, and the one control that undoes it.
 */
function DispositionBanner({
  id,
  disposition,
  announce,
  onChanged,
}: {
  id: string
  /** Null only for a row set aside with no decision on record -- still reopenable, never a dead end. */
  disposition: FindingDetailResponse['disposition']
  /** The person set this aside a moment ago, further down the page: take focus and say so. */
  announce: boolean
  onChanged: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<number | null>(null)
  const heading = useRef<HTMLParagraphElement>(null)

  // Mount only. The Set aside form that was just used sits at the bottom of a long page and has now
  // unmounted, so its focus went to the page body; this banner, directly under the header, is where
  // the decision now lives. Gated on `announce` so opening a set-aside finding from a link does not.
  useEffect(() => {
    if (!announce) return
    heading.current?.focus()
    heading.current?.scrollIntoView?.({ block: 'nearest' })
  }, []) // on arrival only, never on a re-render

  async function reopen() {
    setBusy(true)
    setFailed(null)
    const result = await decideFinding(id, { decision: 'reopen' })
    if (!result.ok) {
      setBusy(false)
      setFailed(result.status)
      return
    }
    // Still busy: this banner unmounts when the refetch shows the finding active again, and the
    // note that replaces it takes focus. Snapping back to "Reopen" first read as nothing happening.
    onChanged()
  }

  const label = disposition ? (DECISION_LABEL[disposition.decision] ?? disposition.decision) : null

  return (
    <Card style={{ marginTop: 'var(--space-4)', borderColor: 'var(--color-neutral-300)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <div style={{ maxWidth: '60ch' }}>
          <p
            ref={heading}
            tabIndex={-1}
            role={announce ? 'status' : undefined}
            style={{ margin: 0, fontSize: 14, fontWeight: 600 }}
          >
            {announce ? 'Recorded. ' : null}
            Set aside
            {disposition ? (
              <>
                {' '}— {label}
                {/* A date, not a time: it was picked as a day ("bring it back on"). See
                    expiryDay for why it is read in UTC. */}
                {disposition.expiresAt ? <> until {expiryDay(disposition.expiresAt)}</> : <> indefinitely</>}
              </>
            ) : null}
          </p>
          {disposition ? (
            <>
              <p style={{ margin: 'var(--space-2) 0 0', fontSize: 13, lineHeight: 1.6 }}>
                {disposition.reason}
              </p>
              <p style={{ margin: 'var(--space-2) 0 0', fontSize: 12, color: 'var(--color-neutral-600)' }}>
                {disposition.decidedBy ? <>by {disposition.decidedBy} · </> : null}
                {formatDate(disposition.decidedAt)}. It is off the active list and out of the severity
                counts{disposition.expiresAt ? ', and returns on the next assessment after it lapses, if it is still present.' : '.'}
              </p>
            </>
          ) : (
            <p style={{ margin: 'var(--space-2) 0 0', fontSize: 12, color: 'var(--color-neutral-600)' }}>
              No decision is on record for this. It is off the active list and out of the severity
              counts; reopen it to put it back.
            </p>
          )}
        </div>
        <Button variant="secondary" onClick={reopen} disabled={busy}>
          {busy ? 'Reopening…' : 'Reopen'}
        </Button>
      </div>
      {failed !== null ? (
        <p className="live-error" role="alert" style={{ marginTop: 'var(--space-3)' }}>{decisionFailure(failed)}</p>
      ) : null}
    </Card>
  )
}

/**
 * Why a decision was not recorded. The decision endpoint's 409 means the finding's check has been
 * retired (0151) -- not describeFailure's 409, which is the reference route's "more than one finding
 * starts with that" and would send somebody typing more characters of a reference they never typed.
 */
function decisionFailure(status: number): string {
  if (status === 409) {
    return 'Defenssive no longer runs the check behind this finding, so it can no longer be changed. '
      + 'Reload to see it as it stands.'
  }
  return describeFailure(status)
}

/** Where the set-aside banner was, once a finding is reopened: says so, and takes focus. */
function ReopenedNote() {
  const note = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    note.current?.focus()
    note.current?.scrollIntoView?.({ block: 'nearest' })
  }, [])
  return (
    <p ref={note} tabIndex={-1} role="status" className="settings-note" style={{ marginTop: 'var(--space-4)' }}>
      Reopened — it is back on the active list and in the severity counts.
    </p>
  )
}

/**
 * The work in flight on an active finding: an owner, a status, and the ticked steps.
 *
 * A full-replace save — the whole intended state goes up together — so the local copy here is the
 * source of truth until Save. `done` is a claim awaiting the next scan; the copy beside it says so,
 * because a green tick that read as "resolved" would be the product saying clean on a say-so.
 *
 * A hook, not a card, since the finding page layout (owner, 2026-10-11): the ticks live on the steps
 * in "How to fix it" and the status, owner and Save in the side panel, and both read one copy.
 */
interface Work {
  status: 'open' | 'in_progress' | 'done'
  owner: string
  steps: Set<number>
  busy: boolean
  saved: boolean
  error: string | null
  dirty: boolean
  setStatus: (value: 'open' | 'in_progress' | 'done') => void
  setOwner: (value: string) => void
  toggle: (index: number) => void
  save: () => Promise<void>
}

function useWork(id: string, workflow: FindingDetailResponse['workflow'], onChanged: () => void): Work {
  const [status, setStatus] = useState<'open' | 'in_progress' | 'done'>(workflow?.status ?? 'open')
  const [owner, setOwner] = useState<string>(workflow?.owner ?? '')
  const [steps, setSteps] = useState<Set<number>>(new Set(workflow?.completedSteps ?? []))
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // What is on screen vs what the server last stored, so the Save button can say whether there is
  // anything to save rather than firing a no-op write.
  const dirty = useMemo(() => {
    const original = {
      status: workflow?.status ?? 'open',
      owner: workflow?.owner ?? '',
      steps: [...(workflow?.completedSteps ?? [])].sort((a, b) => a - b),
    }
    const now = { status, owner, steps: [...steps].sort((a, b) => a - b) }
    return JSON.stringify(original) !== JSON.stringify(now)
  }, [status, owner, steps, workflow])

  function toggle(index: number) {
    setSaved(false)
    setSteps((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  async function save() {
    setBusy(true)
    setError(null)
    setSaved(false)
    const result = await setFindingWorkflow(id, {
      workStatus: status,
      ownerEmail: owner === '' ? null : owner,
      completedSteps: [...steps],
    })
    setBusy(false)
    if (!result.ok) {
      setError(
        result.status === 400
          ? 'That owner is not an active member of this workspace.'
          : describeFailure(result.status),
      )
      return
    }
    setSaved(true)
    onChanged()
  }

  return {
    status,
    owner,
    steps,
    busy,
    saved,
    error,
    dirty,
    setStatus: (value) => { setStatus(value); setSaved(false) },
    setOwner: (value) => { setOwner(value); setSaved(false) },
    toggle,
    save,
  }
}

/** How to fix it, on an active finding: every step a checkbox, then the portal button. */
function WorkSteps({
  work,
  remediation,
  stages,
  portalUrl,
  readOnlyNote,
}: {
  work: Work
  remediation: string[]
  stages: FindingDetailResponse['remediationStages']
  portalUrl: string | null
  /** No evidence card on this page, so the read-only line lives here instead. */
  readOnlyNote: boolean
}) {
  return (
    <Card id="finding-fix" tabIndex={-1} title="How to fix it" className="finding-slot-fix finding-section-target" style={{ marginTop: 'var(--space-4)' }}>
      {stageGroups(remediation, stages).map((group) => (
        <Fragment key={group.start}>
          {group.title ? <h3 className="finding-stage">{group.title}</h3> : null}
          <ol className="remediation-steps" style={{ fontSize: 13, lineHeight: 1.6, margin: 0, paddingLeft: 0, listStyle: 'none' }}>
            {group.steps.map(({ index, text }) => (
              <li key={index} style={{ marginBottom: 'var(--space-2)', display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start' }}>
                {/* Keyed by index, not by the step text: completed_steps is a set of indices, and two
                    identical step strings keyed by content would collide and desync their ticks. */}
                <Checkbox
                  label={renderStep(text)}
                  checked={work.steps.has(index)}
                  onChange={() => work.toggle(index)}
                />
              </li>
            ))}
          </ol>
        </Fragment>
      ))}

      {/* After the steps, where a person reaches the end of reading them -- not beside the owner
          picker, where it was the first thing on the card and invited clicking before reading. */}
      <PortalButton url={portalUrl} />

      {readOnlyNote ? <ReadOnlyNote /> : null}
    </Card>
  )
}

/**
 * The side panel's work card: status, owner, how many steps are ticked, and Save. Beside the page on
 * a wide screen and kept in view while the steps scroll; directly under the steps on a narrow one.
 */
function WorkPanel({ work, total }: { work: Work; total: number }) {
  // Real names for every control. The status buttons sat under a loose span and the owner select
  // had no label at all, so a screen reader announced "Open, toggle button" -- which reads as an
  // action -- and an unnamed combo box.
  const statusLabelId = useId()
  const ownerId = useId()

  const members = useApi(() => getWorkspace(), [])
  const owners: WorkspaceMember[] =
    members.state === 'ready' ? members.data.members.filter((m) => m.status === 'active') : []

  const done = work.steps.size

  return (
    <Card id="finding-progress" tabIndex={-1} title="Progress" className="finding-work finding-section-target" style={{ marginTop: 'var(--space-4)' }}>
      <span id={statusLabelId} className="finding-id-label finding-work-label">Work status</span>
      <div role="group" aria-labelledby={statusLabelId} className="finding-work-status">
        {(['open', 'in_progress', 'done'] as const).map((value) => (
          <button
            key={value}
            type="button"
            className="findings-filter"
            aria-pressed={work.status === value}
            onClick={() => work.setStatus(value)}
          >
            {WORK_STATUS_LABEL[value]}
          </button>
        ))}
      </div>

      <label htmlFor={ownerId} className="finding-id-label finding-work-label">Owner</label>
      <select
        id={ownerId}
        value={work.owner}
        onChange={(e) => work.setOwner(e.target.value)}
        className="finding-work-owner"
      >
        <option value="">Unassigned</option>
        {/* The stored owner may not be in the active-member list (they left, or the list has
            not loaded). Keep it selectable so saving does not silently drop it. */}
        {work.owner !== '' && !owners.some((m) => m.email === work.owner) ? (
          <option value={work.owner}>{work.owner}</option>
        ) : null}
        {owners.map((m) => (
          <option key={m.email} value={m.email}>{m.email}</option>
        ))}
      </select>

      {total > 0 ? (
        <>
          <p className="finding-work-count">
            <strong>{done} of {total}</strong> steps ticked
          </p>
          {/* The words above carry the count; the bar is only a picture of it. */}
          <div className="finding-work-bar" aria-hidden="true">
            <span style={{ width: `${Math.round((done / total) * 100)}%` }} />
          </div>
        </>
      ) : null}

      {/* Still the rule it always was -- a tick is a note, only a scan closes a finding -- in the
          owner's words (identity review, 2026-09-25). */}
      <p className="finding-work-note">
        Ticks are for your own tracking. The finding closes when the next assessment confirms the fix.
      </p>

      <div className="finding-work-save">
        <Button variant="primary" onClick={() => void work.save()} disabled={work.busy || !work.dirty}>
          {work.busy ? 'Saving…' : 'Save progress'}
        </Button>
        {work.saved && !work.dirty ? (
          <span style={{ fontSize: 12, color: 'var(--color-neutral-600)' }}>Saved.</span>
        ) : null}
      </div>

      {work.error ? <p className="live-error" role="alert" style={{ marginTop: 'var(--space-3)' }}>{work.error}</p> : null}
    </Card>
  )
}

/**
 * The page's one "only reads" line, for a finding with no evidence card to carry it (the evidence
 * footer says it otherwise). Once is enough; three times read as protesting too much. It said
 * "Defenssive has read-only access to your tenant", a capability the Terms and the privacy notice no
 * longer claim (two Exchange permissions that are not read-only by name); what it does is only read.
 */
function ReadOnlyNote() {
  return (
    <p style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 'var(--space-3)' }}>
      Defenssive only reads: it makes no change in your tenant, so these changes are yours to make.
    </p>
  )
}

/**
 * Why an active finding still shows an old last-seen date: the latest finished assessment did not
 * re-check it. The server decides that (finding.last_seen_at against the scan, with the scan's own
 * scan_check_status reason); this only words it, one sentence per reason.
 *
 * A withheld check (0152) waits on another finding and never resolves anything while it waits, so
 * the finding stays open on purpose -- the notice names what to do first, linked to it. A check that
 * could not read, or no recorded reason, gets the plain version. None of the three claims the
 * finding is fixed, or that it is still true today: only that no assessment has confirmed it since.
 */
function RecheckNotice({ recheck }: { recheck: FindingRecheck }) {
  // A day reads better than a timestamp, unless both land on the same day and the sentence would
  // say "since 8 September ... on 8 September".
  const sameDay = formatDay(recheck.lastCheckedAt) === formatDay(recheck.latestAssessmentAt)
  const when = (value: string) => (sameDay ? formatDate(value) : formatDay(value))
  const since = when(recheck.lastCheckedAt)
  const latest = when(recheck.latestAssessmentAt)
  const waitsOn = recheck.reason === 'withheld' ? recheck.waitsOn : null

  return (
    <p
      role="note"
      className={recheck.reason === 'could_not_run' ? 'finding-recheck is-unread' : 'finding-recheck'}
    >
      <strong>Not re-checked since {since}.</strong>{' '}
      {waitsOn ? (
        <>
          This check waits for ‘
          <Link to={`/recommendations?q=${encodeURIComponent(waitsOn.title)}`}>{waitsOn.title}</Link>
          ’ to be resolved before it re-reads these settings.
        </>
      ) : recheck.reason === 'could_not_run' ? (
        <>
          The latest finished assessment, on {latest}, could not read these settings. This stays open
          until an assessment can.
        </>
      ) : (
        // No reason on record: part of an area that did not finish, a read that came back short, a
        // licence gap. All the page can honestly say is that the latest assessment did not confirm it.
        // "Finished": the date is the newest complete or partial scan, and a newer one that failed
        // outright (which the dashboard shows as the latest) re-checked nothing.
        <>
          The latest finished assessment, on {latest}, did not confirm this either way. It stays
          open until an assessment does.
        </>
      )}
    </p>
  )
}

/**
 * The framework references as chips, and the catalogue's further reading as links. One card, since
 * both answer "where does this come from and where do I read more"; either half may be absent.
 */
function References({ references, docLinks }: { references: string[]; docLinks: FindingDocLink[] }) {
  const referencesId = useId()
  const learnMoreId = useId()
  const chips = referenceChips(references)
  const links = docLinks.filter((link) => isWebAddress(link.url))
  if (chips.length === 0 && links.length === 0) return null

  return (
    <Card title="References" style={{ marginTop: 'var(--space-4)' }}>
      {chips.length > 0 ? (
        <section aria-labelledby={referencesId}>
          <h3 id={referencesId} className="finding-references-heading">Control references</h3>
          <ul className="finding-reference-chips">
            {chips.map((reference, index) => (
              <li key={`${reference}-${index}`}>
                <Tag tone="neutral">{reference}</Tag>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {links.length > 0 ? (
        <section aria-labelledby={learnMoreId}>
          <h3 id={learnMoreId} className="finding-references-heading">Learn more</h3>
          <ul className="finding-learn-more">
            {links.map((link, index) => (
              <li key={`${link.url}-${index}`}>
                <a href={link.url} target="_blank" rel="noopener noreferrer">
                  {link.title || link.url}
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </Card>
  )
}

/**
 * One chip per framework reference. The server already splits the catalogue's single string; this
 * splits again so a response from an older server (the whole string as one entry) renders the same,
 * and it is a no-op on one that did. The separators are matched with their spaces, so a hyphen inside
 * a reference (AC-6, IA-2(1)) never splits it.
 */
export function referenceChips(references: string[]): string[] {
  return references
    .flatMap((reference) => reference.split(/ · | - | – | — |; /))
    .map((reference) => reference.trim())
    .filter((reference) => reference !== '')
}

/** Only http(s) addresses become links; anything else in the catalogue is not somewhere to send a person. */
function isWebAddress(url: string | null | undefined): url is string {
  if (!url) return false
  try {
    const protocol = new URL(url).protocol
    return protocol === 'https:' || protocol === 'http:'
  } catch {
    return false
  }
}

/**
 * The first day an accepted risk may lapse on, as the date input's own YYYY-MM-DD: tomorrow, in
 * the browser's LOCAL calendar. Not toISOString().slice(0, 10), which is the UTC date and is
 * tomorrow's date for part of every evening west of UTC -- the off-by-one this replaces.
 */
function tomorrowLocal(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * Setting a finding aside: a risk accepted, a control that does not apply, or a false positive.
 * A decision, with a reason that is the point of it, that takes the finding off the active list.
 */
function SetAside({ id, onChanged }: { id: string; onChanged: () => void }) {
  const [mode, setMode] = useState<'accept_risk' | 'not_applicable' | 'suppress' | null>(null)
  const [reason, setReason] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [invalid, setInvalid] = useState<'reason' | 'date' | null>(null)
  const reasonId = useId()
  const dateId = useId()
  const dateHintId = useId()
  const errorId = useId()
  const minDate = tomorrowLocal()

  function reset() {
    setMode(null)
    setReason('')
    setExpiresAt('')
    setError(null)
    setInvalid(null)
  }

  async function confirm() {
    if (mode === null) return
    if (reason.trim() === '') {
      setInvalid('reason')
      setError('Add a reason so whoever reviews this next knows why.')
      return
    }
    const lapses = mode === 'accept_risk' && expiresAt !== ''
    // Caught here, by name. The server refuses a lapse date that is not in the future, and all
    // that came back was "check the reason and any expiry date".
    if (lapses && expiresAt < minDate) {
      setInvalid('date')
      setError('Pick a date after today, or leave it blank.')
      return
    }
    setBusy(true)
    setError(null)
    setInvalid(null)
    const result = await decideFinding(id, {
      decision: mode,
      reason: reason.trim(),
      // An expiry only means anything for an accepted risk — on lapse the finding returns on the
      // next assessment that still sees it (enforced on re-observation, not autonomously).
      // A CALENDAR DAY, stored as noon UTC on it and read back in UTC (expiryDay; the weekly digest
      // prints the UTC date too). Midnight in any zone lands on the neighbouring day for someone:
      // UTC midnight showed the day before west of UTC, local midnight put the day before in the
      // digest east of UTC. Noon UTC is the same date from UTC-12 to UTC+12, and is still in the
      // future for tomorrow's date anywhere, which the server requires.
      expiresAt: lapses ? `${expiresAt}T12:00:00.000Z` : null,
    })
    if (!result.ok) {
      setBusy(false)
      setError(
        result.status === 400
          ? 'That could not be recorded — check the reason and any expiry date.'
          : decisionFailure(result.status),
      )
      return
    }
    // Not reset(). Snapping back to the three unchosen buttons read as the action being cancelled.
    // This card stays "Recording…" until the refetch shows the finding set aside and removes it;
    // the banner that replaces it at the top of the page takes focus and says "Recorded".
    onChanged()
  }

  return (
    <Card title="Set this aside" style={{ marginTop: 'var(--space-4)' }}>
      <p style={{ fontSize: 13, lineHeight: 1.6, margin: '0 0 var(--space-3)', maxWidth: '72ch', color: 'var(--color-neutral-600)' }}>
        Takes this off the active list and out of the severity counts, with a reason kept on record.
        This doesn&apos;t change anything in your tenant. You can reopen it at any time.
      </p>

      <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
        {(['accept_risk', 'not_applicable', 'suppress'] as const).map((value) => (
          <button
            key={value}
            type="button"
            className="findings-filter"
            aria-pressed={mode === value}
            onClick={() => { setMode(value); setError(null) }}
          >
            {DECISION_LABEL[value]}
          </button>
        ))}
      </div>

      {mode !== null ? (
        <div style={{ marginTop: 'var(--space-3)' }}>
          {/* A real label: the question is the reason box's name. It was a loose span, so a screen
              reader announced the box only by its placeholder, never what it asks. */}
          <label htmlFor={reasonId} className="finding-id-label" style={{ display: 'block', marginBottom: 'var(--space-1)' }}>
            {DECISION_PROMPT[mode]}
          </label>
          <textarea
            id={reasonId}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value)
              if (invalid === 'reason') setInvalid(null)
            }}
            rows={3}
            maxLength={2000}
            aria-invalid={invalid === 'reason' || undefined}
            aria-describedby={invalid === 'reason' ? errorId : undefined}
            style={{ width: '100%', maxWidth: '72ch', fontSize: 13, padding: '8px', lineHeight: 1.5 }}
            placeholder="This is kept on record for whoever reviews it next."
          />

          {mode === 'accept_risk' ? (
            <div style={{ marginTop: 'var(--space-2)' }}>
              <label htmlFor={dateId} className="finding-id-label" style={{ marginRight: 'var(--space-2)' }}>
                Bring it back on (optional)
              </label>
              <input
                id={dateId}
                type="date"
                min={minDate}
                value={expiresAt}
                onChange={(e) => {
                  setExpiresAt(e.target.value)
                  if (invalid === 'date') setInvalid(null)
                }}
                aria-invalid={invalid === 'date' || undefined}
                aria-describedby={invalid === 'date' ? `${dateHintId} ${errorId}` : dateHintId}
                style={{ fontSize: 13, padding: '6px 8px' }}
              />
              <span id={dateHintId} style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginLeft: 'var(--space-2)' }}>
                Left blank, the acceptance stands until you reopen it.
              </span>
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
            <Button variant="primary" onClick={confirm} disabled={busy}>
              {busy ? 'Recording…' : `Set aside — ${DECISION_LABEL[mode]}`}
            </Button>
            <Button variant="ghost" onClick={reset} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {error ? (
        <p id={errorId} className="live-error" role="alert" style={{ marginTop: 'var(--space-3)' }}>
          {error}
        </p>
      ) : null}
    </Card>
  )
}

/**
 * An accepted risk's lapse date, as the day it was picked on. Stored as noon UTC on that day (see
 * SetAside), so it is read in UTC: in the viewer's zone a decision made in London would show a
 * different day in Los Angeles. Rows stored earlier at UTC midnight read correctly the same way.
 */
function expiryDay(value: string): string {
  const when = new Date(value)
  return Number.isNaN(when.getTime())
    ? value
    : when.toLocaleDateString(undefined, { dateStyle: 'long', timeZone: 'UTC' })
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  resolved: 'Resolved',
  suppressed: 'Set aside',
  // Not "Retired" or "Closed": both read as done. What is true is that we stopped looking.
  withdrawn: 'No longer assessed',
}

// PowerShell cmdlets in a remediation step, rendered as monospace so the command a person is meant
// to run stands out from the prose around it and is easy to copy.
const CMDLET = /\b(?:Set|Get|New|Remove|Enable|Disable|Connect|Update|Add|Search)-[A-Za-z]+\b/g

function renderStep(text: string): ReactNode {
  const parts: ReactNode[] = []
  let last = 0
  for (const match of text.matchAll(CMDLET)) {
    const at = match.index ?? 0
    if (at > last) parts.push(text.slice(last, at))
    parts.push(
      <code
        key={at}
        style={{ fontFamily: 'var(--font-mono)', fontSize: '0.92em', background: 'var(--color-neutral-100)', borderRadius: 3, padding: '0 4px', whiteSpace: 'nowrap' }}
      >
        {match[0]}
      </code>,
    )
    last = at + match[0].length
  }
  if (last < text.length) parts.push(text.slice(last))
  return parts.length > 0 ? parts : text
}

/**
 * The jump-off to the Microsoft admin surface, after the steps. Nothing for a DNS or tenant-specific
 * fix -- then the remediation prose is the only guide, which is correct, not a gap.
 *
 * Named for where it goes ("Open in Microsoft Entra"), read from the address itself: "Open in
 * Microsoft admin" said nothing about which of half a dozen admin centres a person was about to land in.
 */
function PortalButton({ url }: { url: string | null | undefined }) {
  if (!isWebAddress(url)) return null
  return (
    <p className="finding-portal">
      <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary finding-portal-link">
        {portalLabel(url)}
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M7 17L17 7M17 7H8M17 7v9" />
        </svg>
        <span className="sr-only"> (opens in a new tab)</span>
      </a>
    </p>
  )
}

/** The admin centre a portal address belongs to, by host, as the button's words. */
export function portalLabel(url: string): string {
  let host: string
  try {
    host = new URL(url).hostname.toLowerCase()
  } catch {
    return 'Open the Microsoft admin page'
  }

  if (host === 'entra.microsoft.com' || host === 'aad.portal.azure.com') return 'Open in Microsoft Entra'
  if (host === 'security.microsoft.com') return 'Open in Microsoft Defender'
  if (host === 'purview.microsoft.com' || host === 'compliance.microsoft.com') return 'Open in Microsoft Purview'
  if (host === 'intune.microsoft.com' || host === 'endpoint.microsoft.com') return 'Open in Microsoft Intune'
  if (host === 'admin.exchange.microsoft.com') return 'Open in the Exchange admin center'
  if (host === 'admin.teams.microsoft.com') return 'Open in the Teams admin center'
  if (host.endsWith('-admin.sharepoint.com')) return 'Open in the SharePoint admin center'
  if (host === 'admin.microsoft.com' || host === 'admin.cloud.microsoft') return 'Open in the Microsoft 365 admin center'
  if (host === 'portal.azure.com') return 'Open in the Azure portal'
  // Microsoft Learn's own links to SharePoint admin center pages (0201): that admin center's address
  // is the tenant's own, so a catalogue can only reach it through these.
  if (host === 'go.microsoft.com' && SHAREPOINT_ADMIN_LINKS.has(new URL(url).searchParams.get('linkid') ?? '')) {
    return 'Open in the SharePoint admin center'
  }
  return 'Open the Microsoft admin page'
}

/** Sharing, Access control and Settings in the SharePoint admin center, as Microsoft Learn links them. */
const SHAREPOINT_ADMIN_LINKS = new Set(['2185222', '2185071', '2185072'])

const DECISION_LABEL: Record<string, string> = {
  accept_risk: 'Accept the risk',
  not_applicable: 'Not applicable',
  // The API value stays 'suppress'. The label is what the reason box already asks ("Why is this a
  // false positive?"); "Dismiss" read as a way to make a finding go away.
  suppress: 'False positive',
  reopen: 'Reopened',
}

const DECISION_PROMPT: Record<string, string> = {
  accept_risk: 'Why is this risk acceptable?',
  not_applicable: 'Why does this not apply to your tenant?',
  suppress: 'Why is this a false positive?',
}

const WORK_STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  in_progress: 'In progress',
  done: 'Done',
}
